// src/pages/Events/EventView.js - UPDATING EXISTING FILE
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import { Helmet } from "react-helmet-async";
import { spacing } from "@mui/system";
import styled from "@emotion/styled";
import { Link } from "react-router-dom";
import { useParams, useLocation, NavLink } from "react-router-dom";
import { axiosInstance } from "../../utils/axios";
import MenuList from "@mui/material/MenuList";
import ListItemText from "@mui/material/ListItemText";
import People from "../../pages/Events/People";
import EventEntry from "../../pages/Events/EventEntry";
import RegistrationForm from "../../pages/Events/RegistrationForm";
import Sessions from "../../pages/Events/Sessions";
import EventSpacesComponent from "../../components/Events/Events/EventSpaces/EventSpaces";
import BrandingComponent from "../../components/Events/Events/Branding/Branding";
import AddTypeDialog from "./AddType.js";
import { Snackbar, Alert, CircularProgress } from "@mui/material";
import { useSpace } from "../../contexts/SpaceContext";
import { HTMLContent } from "../../utils/HTMLParser";
import moment from "moment-timezone";

import {
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  Divider as MuiDivider,
  Typography,
  Grid,
  Button,
  CardContent,
  Paper as MuiPaper,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  DialogActions,
  TextField,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  List,
  ListItem,
} from "@mui/material";

import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Share,
} from "@mui/icons-material";

import VisibilityIcon from "@mui/icons-material/Visibility";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { sdkConfig } from "../../config";

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const ToolbarTitle = styled.div`
  min-width: 620px;
  @media (max-width: 960px) {
    min-width: 100%;
  }
`;

function EventHeader({ event }) {
  const [anchorEl1, setAnchorEl1] = useState(null);

  const handleClickShowing = (event) => {
    setAnchorEl1(event.currentTarget);
  };

  const handleCloseShowing = () => {
    setAnchorEl1(null);
  };

  if (!event) return null;

  const startDate = new Date(parseInt(event.startDate));
  const endDate = new Date(parseInt(event.endDate));
  const startTime = new Date(event.startTime);
  const endTime = new Date(event.endTime);

  const getEventStatus = (startDate, endDate) => {
    const now = new Date();
    if (now < startDate) return "Upcoming";
    if (now > endDate) return "Completed";
    return "Ongoing";
  };

  // Convert backend date + time into a proper moment in timezone
  const startDateTime = moment.tz(
    `${moment(Number(event.startDate)).format("YYYY-MM-DD")}T${
      event.startTime
    }`,
    event.timezone
  );

  const endDateTime = moment.tz(
    `${moment(Number(event.endDate)).format("YYYY-MM-DD")}T${event.endTime}`,
    event.timezone
  );

  return (
    <Card mb={6}>
      <CardContent>
        <Toolbar style={{ paddingLeft: "0" }}>
          <ToolbarTitle>
            <Typography
              variant="subtitle1"
              style={{ paddingLeft: "12px", fontWeight: "bold" }}
            >
              {event.name}
              <small
                style={{
                  fontWeight: "normal",
                  position: "relative",
                  bottom: "1px",
                  paddingLeft: "10px",
                  color: "rgb(244, 67, 54)",
                }}
              >
                {getEventStatus(startDate, endDate)}
              </small>
              <Typography
                color="success"
                component="div"
                style={{
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: "12px",
                  marginTop: "4px",
                }}
              >
                <CalendarMonthOutlinedIcon
                  fontSize="small"
                  style={{ marginRight: "4px" }}
                />
                <small>
                  {startDateTime.format("ddd, MMM D, YYYY")},{" "}
                  {startDateTime.format("hh:mm A")} -{" "}
                  {endDateTime.format("ddd, MMM D, YYYY")},{" "}
                  {endDateTime.format("hh:mm A")} • {event.timezone}
                </small>{" "}
              </Typography>
            </Typography>
          </ToolbarTitle>
          <Grid container display="block" textAlign="right">
            <Grid container>
              <Grid item md={12} className="filter">
                <Button
                  variant="contained"
                  color="inherit"
                  sx={{ mr: 2, display: { xs: "none", md: "inline-flex" } }}
                >
                  <Share />
                  Share Event
                </Button>
                <Button
                  variant="contained"
                  color="inherit"
                  sx={{ mr: 2, display: { xs: "none", md: "inline-flex" } }}
                >
                  <VisibilityIcon />
                  View Event
                </Button>
                <Button
                  variant="contained"
                  sx={{ mr: 2, display: { xs: "none", md: "inline-flex" } }}
                >
                  <PlayArrowIcon />
                  Start Event
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  sx={{ mr: 2, display: { xs: "none", md: "inline-flex" } }}
                  onClick={() => {
                    const url = `${sdkConfig.sdkURI}/event.html?eventId=${event.id}&name=Host&category=Host`;
                    window.open(url, "_blank", "noopener,noreferrer");
                  }}
                >
                  <OpenInNewIcon sx={{ mr: 0.5 }} />
                  Launch Virtual Event
                </Button>
                <IconButton
                  aria-label="Edit"
                  size="large"
                  aria-owns={anchorEl1 ? "simple-menu-showing" : undefined}
                  aria-haspopup="true"
                  onClick={handleClickShowing}
                  variant="contained"
                  color="secondary"
                  sx={{ mr: 2, display: { xs: "block", md: "none" } }}
                >
                  <FilterAltIcon />
                </IconButton>
                <IconButton
                  aria-label="View"
                  size="large"
                  aria-owns={anchorEl1 ? "simple-menu-showing" : undefined}
                  aria-haspopup="true"
                  onClick={handleClickShowing}
                  variant="contained"
                  color="secondary"
                  sx={{ mr: 2, display: { xs: "block", md: "none" } }}
                >
                  <VisibilityIcon />
                </IconButton>
                <Menu
                  id="simple-menu-showing"
                  anchorEl={anchorEl1}
                  open={Boolean(anchorEl1)}
                  onClose={handleCloseShowing}
                >
                  <MenuItem>All</MenuItem>
                  <MenuItem>Upcoming</MenuItem>
                  <MenuItem>Ongoing</MenuItem>
                  <MenuItem>Paused</MenuItem>
                  <MenuItem>Completed</MenuItem>
                </Menu>
              </Grid>
            </Grid>
          </Grid>
        </Toolbar>
      </CardContent>
    </Card>
  );
}

function CustomTabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`event-tabpanel-${index}`}
      aria-labelledby={`event-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component="div">{children}</Typography>
        </Box>
      )}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `event-tab-${index}`,
    "aria-controls": `event-tabpanel-${index}`,
  };
}

function RegistrationTab({ eventId }) {
  const [activeTab, setActiveTab] = useState("Event Entry");

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <Paper sx={{ height: "100%" }}>
          <MenuList>
            <MenuItem
              onClick={() => handleTabChange("Event Entry")}
              selected={activeTab === "Event Entry"}
              sx={{
                borderLeft: "3px solid",
                borderLeftColor:
                  activeTab === "Event Entry" ? "primary.main" : "transparent",
                backgroundColor:
                  activeTab === "Event Entry"
                    ? "rgba(0, 0, 0, 0.04)"
                    : "transparent",
                minHeight: "50px",
              }}
            >
              <ListItemText primary="Event Entry" />
            </MenuItem>
            <MenuItem
              onClick={() => handleTabChange("Registration Form")}
              selected={activeTab === "Registration Form"}
              sx={{
                borderLeft: "3px solid",
                borderLeftColor:
                  activeTab === "Registration Form"
                    ? "primary.main"
                    : "transparent",
                backgroundColor:
                  activeTab === "Registration Form"
                    ? "rgba(0, 0, 0, 0.04)"
                    : "transparent",
                minHeight: "50px",
              }}
            >
              <ListItemText primary="Registration Form" />
            </MenuItem>
          </MenuList>
        </Paper>
      </Grid>
      <Grid item xs={12} md={9}>
        {activeTab === "Event Entry" ? (
          <EventEntry eventId={eventId} />
        ) : (
          <RegistrationForm eventId={eventId} />
        )}
      </Grid>
    </Grid>
  );
}

function PeopleTab({ eventId }) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([
    { id: "Attendee", name: "Attendees" },
    { id: "Speaker", name: "Speaker" },
    { id: "Host", name: "Host" },
    { id: "Co Host", name: "Co Host" },
  ]);
  const [addTypeOpen, setAddTypeOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showNotification = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      console.log("Fetching attendees to extract categories...");

      // This query will get all attendees
      const response = await axiosInstance.post("/graphql", {
        query: `
          query GetEventAttendees($eventId: ID!) {
            getEventAttendees(eventId: $eventId) {
              id
              category
            }
          }
        `,
        variables: { eventId },
      });

      console.log("Response from getEventAttendees:", response);

      if (response.errors) {
        throw new Error(
          response.errors[0].message || "Failed to fetch categories"
        );
      }

      // Extract unique categories from attendees
      const attendees = response.data?.getEventAttendees || [];
      console.log("Fetched attendees:", attendees);

      // Get all unique categories from attendees
      const uniqueCategories = [
        ...new Set(attendees.map((a) => a.category).filter(Boolean)),
      ];
      console.log("Unique categories from attendees:", uniqueCategories);

      // Create a set of categories combining default ones and existing ones
      const defaultCategories = ["Attendee", "Speaker", "Host", "Co Host"];
      const allCategories = [
        ...new Set([...defaultCategories, ...uniqueCategories]),
      ];

      // Convert to array of objects for state
      const categoryObjects = allCategories.map((category) => ({
        id: category,
        name: category === "Attendee" ? "Attendees" : category,
      }));

      console.log("Final category objects:", categoryObjects);
      setCategories(categoryObjects);

      // Select a default category if none is selected
      if (!selectedCategory && categoryObjects.length > 0) {
        console.log("Setting default category:", categoryObjects[0]);
        setSelectedCategory(categoryObjects[0]);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      showNotification(error.message || "Failed to fetch categories", "error");
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch of categories
  useEffect(() => {
    if (eventId) {
      fetchCategories();
    }
  }, [eventId]);

  const handleAddCategory = (newCategory) => {
    console.log("Adding new category:", newCategory);

    // Check if category already exists (case-insensitive)
    if (
      categories.some(
        (c) => c.name.toLowerCase() === newCategory.name.toLowerCase()
      )
    ) {
      showNotification(
        `Category "${newCategory.name}" already exists`,
        "error"
      );
      return;
    }

    // Add new category to the list
    const updatedCategories = [...categories, newCategory];
    setCategories(updatedCategories);

    // Set the new category as selected
    setSelectedCategory(newCategory);

    showNotification(`Category "${newCategory.name}" added successfully`);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <Paper sx={{ height: "100%", pb: 2 }}>
          {loading ? (
            <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <List component="nav" sx={{ pt: 0 }}>
                {categories.map((category) => (
                  <ListItem
                    button
                    key={category.id}
                    selected={selectedCategory?.id === category.id}
                    onClick={() => setSelectedCategory(category)}
                    sx={{
                      pl: 2,
                      borderLeft: "3px solid",
                      borderLeftColor:
                        selectedCategory?.id === category.id
                          ? "primary.main"
                          : "transparent",
                      backgroundColor:
                        selectedCategory?.id === category.id
                          ? "rgba(0, 0, 0, 0.04)"
                          : "transparent",
                    }}
                  >
                    <ListItemText primary={category.name} />
                  </ListItem>
                ))}
              </List>
              {categories.length === 0 && (
                <Box sx={{ p: 3, textAlign: "center" }}>
                  <Typography variant="body2" color="textSecondary">
                    No categories found
                  </Typography>
                </Box>
              )}
            </>
          )}
          {/* <Box sx={{ px: 2, mt: 2 }}> */}
          {/*   <Button */}
          {/*     size="small" */}
          {/*     variant="contained" */}
          {/*     color="primary" */}
          {/*     onClick={() => setAddTypeOpen(true)} */}
          {/*     startIcon={<AddIcon />} */}
          {/*     fullWidth */}
          {/*   > */}
          {/*     Add Category */}
          {/*   </Button> */}
          {/* </Box> */}
        </Paper>
      </Grid>
      <Grid item xs={12} md={9}>
        {selectedCategory ? (
          <People selectedType={selectedCategory} eventId={eventId} />
        ) : (
          <Paper sx={{ p: 4, textAlign: "center", height: "100%" }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {categories.length === 0
                ? "Please add a category to start managing attendees"
                : "Please select a category from the left"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {categories.length === 0
                ? "Categories help you organize different types of attendees such as Hosts, Speakers, and Participants"
                : "Select a category to view and manage its attendees"}
            </Typography>
          </Paper>
        )}
      </Grid>

      <AddTypeDialog
        open={addTypeOpen}
        handleClose={() => setAddTypeOpen(false)}
        eventId={eventId}
        onAddType={handleAddCategory}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

function SessionTab({ eventId }) {
  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={3}>
        <Paper>
          <MenuList>
            <MenuItem
              selected
              sx={{
                borderLeft: "3px solid",
                borderLeftColor: "primary.main",
                backgroundColor: "rgba(0, 0, 0, 0.04)",
                minHeight: "50px",
              }}
            >
              <ListItemText primary="Session List" />
            </MenuItem>
            <MenuItem sx={{ minHeight: "50px" }}>
              <ListItemText primary="Session Recordings" />
            </MenuItem>
            <MenuItem sx={{ minHeight: "50px" }}>
              <ListItemText primary="Pre-recorded Videos" />
            </MenuItem>
            <MenuItem sx={{ minHeight: "50px" }}>
              <ListItemText primary="Session Settings" />
            </MenuItem>
          </MenuList>
        </Paper>
      </Grid>
      <Grid item xs={12} md={9}>
        <Sessions eventId={eventId} />
      </Grid>
    </Grid>
  );
}

export default function EventView() {
  const { id: eventId } = useParams();
  const location = useLocation();
  const { currentSpace } = useSpace();
  const [value, setValue] = useState(0);
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const queryParams = new URLSearchParams(location.search);
  const spaceId = queryParams.get("spaceId") || currentSpace?.id;

  useEffect(() => {
    const fetchEventData = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.post("/graphql", {
          query: `
            query GetEventData($eventId: ID!, $spaceId: ID) {
              getEvent(id: $eventId, spaceId: $spaceId) {
                id
                name
                startDate
                endDate
                startTime
                endTime
                timezone
                organizedBy
                supportEmail
                about
                spaceId
              }
            }
          `,
          variables: {
            eventId,
            spaceId,
          },
        });

        if (response.errors) {
          throw new Error(
            response.errors[0].message || "Failed to fetch event"
          );
        }

        if (response.data.getEvent) {
          setEventData(response.data.getEvent);
        } else {
          throw new Error("Event not found");
        }
      } catch (error) {
        console.error("Error fetching event:", error);
        setError(error.message || "Failed to fetch event data");
        setAlert({
          open: true,
          message: error.message || "Failed to fetch event data",
          severity: "error",
        });
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchEventData();
    }
  }, [eventId, spaceId]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!eventData) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">No event data found</Alert>
      </Box>
    );
  }

  return (
    <React.Fragment>
      <Helmet title={`Event View - ${eventData.name}`} />

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={() => setAlert({ ...alert, open: false })}
      >
        <Alert
          onClose={() => setAlert({ ...alert, open: false })}
          severity={alert.severity}
        >
          {alert.message}
        </Alert>
      </Snackbar>

      <Grid justifyContent="space-between" container spacing={10}>
        <Grid item>
          <Typography variant="h3" gutterBottom display="inline">
            Event View
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/events/dashboard">
              Dashboard
            </Link>
            <Link component={NavLink} to="/events/events">
              Events List
            </Link>
            <Typography component="div"> Event View</Typography>
          </Breadcrumbs>
        </Grid>
        <Grid item>
          <div>
            <Button
              variant="contained"
              color="inherit"
              sx={{ mx: "0.5rem" }}
              component={Link}
              to="/events/events"
            >
              <ArrowBackIcon />
              Back
            </Button>
          </div>
        </Grid>
      </Grid>
      <Divider my={6} />
      <EventHeader event={eventData} />
      <Box sx={{ width: "100%" }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="event tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Summary" {...a11yProps(0)} />
            <Tab label="Registration" {...a11yProps(1)} />
            <Tab label="People" {...a11yProps(2)} />
            <Tab label="Sessions" {...a11yProps(3)} />
            <Tab label="Event Spaces" {...a11yProps(4)} />
            <Tab label="Branding" {...a11yProps(5)} />
            <Tab label="Integrations" {...a11yProps(6)} />
            <Tab label="Workflows" {...a11yProps(7)} />
            <Tab label="Analytics" {...a11yProps(8)} />
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Event Summary
            </Typography>
            <Typography variant="body1">
              {eventData.about ? (
                <HTMLContent html={eventData.about} />
              ) : (
                <Typography variant="body1">No summary available</Typography>
              )}
            </Typography>
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <RegistrationTab eventId={eventId} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          <PeopleTab eventId={eventId} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={3}>
          <SessionTab eventId={eventId} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={4}>
          <EventSpacesComponent eventId={eventId} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={5}>
          <BrandingComponent eventId={eventId} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={6}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Integrations
            </Typography>
            <Typography variant="body1">
              Integrations module is under development
            </Typography>
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={7}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Workflows
            </Typography>
            <Typography variant="body1">
              Workflows module is under development
            </Typography>
          </Box>
        </CustomTabPanel>
        <CustomTabPanel value={value} index={8}>
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>
              Analytics
            </Typography>
            <Typography variant="body1">
              Analytics module is under development
            </Typography>
          </Box>
        </CustomTabPanel>
      </Box>
    </React.Fragment>
  );
}
